import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from '../../../../../../ui/toast';
import { setPreScreenQuestions } from '../../../../../../utils/api_service/assessment_api';
import { searchPreScreenQuestions } from '../../../../../../utils/api_service/pre_screen_api';
import { debounce } from '../../../../../../utils/helpers/debounce';
import {
  Assessment,
  PreScreenQuestion,
  PreScreenQuestionBank,
  PreScreenQuestionType,
} from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
const questionType: SelectOptions[] = [
  {
    label: 'Numeric',
    value: 'numeric',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Video',
    value: 'video',
  },
];
interface PreScreenQuestionsProps {
  assessmentInfo: Assessment;
  setAssessmentInfo: Dispatch<SetStateAction<Assessment | undefined>>;
}

export const usePreScreenQuestions = (props: PreScreenQuestionsProps) => {
  const { assessmentInfo, setAssessmentInfo } = props;
  const [questions, setQuestions] = useState<PreScreenQuestion[]>(
    assessmentInfo.preScreen ?? [],
  );
  const [newQuestionType, setNewQuestionType] =
    useState<PreScreenQuestionType>('numeric');
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [choices, setChoices] = useState<string[]>(['']);
  const [editQuestionType, setEditQuestionType] =
    useState<PreScreenQuestionType>('numeric');
  const [editQuestionText, setEditQuestionText] = useState<string>('');
  const [editChoices, setEditChoices] = useState<string[]>(['']);
  const [editIndex, setEditIndex] = useState<string | null>(null);
  const [searchQuestions, setSearchQuestions] = useState<
    PreScreenQuestionBank[]
  >([]);
  const [defaultOptions, setDefaultOptions] = useState<SelectOptions[]>([]);

  const getQuestionTypeLabel = (value: string): string => {
    const found = questionType.find((type) => type.value === value);
    return found ? found.label : value;
  };
  const createLabel = (question: string, questionType: string) => {
    return (
      <div className='flex items-center'>
        <div className='w-10/12'>
          <span>{ question }</span>
        </div>
        <div className='w-1/12 text-left'>
          <span className='text-gray-500 text-sm'>
            { getQuestionTypeLabel(questionType) }
          </span>
        </div>
      </div>
    );
  };
  const fetchOptions = async (inputValue: string): Promise<SelectOptions[]> => {
    try {
      const response = await searchPreScreenQuestions(inputValue, null, 1, 10);
      if (response.ok) {
        const data = await response.json();
        setSearchQuestions(data.questions);
        return data.questions.map((question: PreScreenQuestionBank) => ({
          value: question.id,
          label: createLabel(question.question, question.questionType),
        }));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
        return [];
      }
    } catch (error) {
      toast.error(`Error fetching questions: ${error}`, {
        position: 'bottom-center',
      });
      return [];
    }
  };
  useEffect(() => {
    const loadDefaultOptions = async () => {
      const options = await fetchOptions('');
      setDefaultOptions(options);
    };

    loadDefaultOptions();
  }, []);

  const loadOptionsDebounced = debounce(
    async (
      inputValue: string,
      callback: (options: SelectOptions[]) => void,
    ) => {
      const options = await fetchOptions(inputValue);
      callback(options);
    },
    1000,
  );

  const generateUniqueKey = () => {
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .substring(2)}`;
  };

  const persistQuestions = async (updatedQuestions: PreScreenQuestion[]) => {
    try {
      const response = await setPreScreenQuestions(
        assessmentInfo.id,
        updatedQuestions,
      );
      if (response.ok) {
        const assessment: Assessment = await response.json();
        setAssessmentInfo(assessment);
        toast.dismiss(); // Clear any ongoing toasts to prevent clutter
        toast.success('Questions auto-saved', {
          position: 'bottom-center',
          autoClose: 2000,
        });
      } else {
        toast.error('Auto-save failed', { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Auto-save failed. Connection error.', {
        position: 'bottom-center',
      });
    }
  };

  const handleAddQuestion = () => {
    if (!newQuestionText.trim()) return;

    const filteredChoices = choices.filter((choice) => choice.trim() !== '');
    const newQuestion: PreScreenQuestion = {
      questionId: generateUniqueKey(),
      questionType: newQuestionType,
      question: newQuestionText,
      ...(newQuestionType === 'multiple-choice' && {
        choices: [...filteredChoices],
      }),
    };
    const updatedQuestions = [...questions, newQuestion];
    setQuestions(updatedQuestions);
    setNewQuestionText('');
    setChoices(['']);
    persistQuestions(updatedQuestions);
  };

  const handleChoiceChange = (index: number, value: string, edit: boolean) => {
    if (edit) {
      const newChoices = [...editChoices];
      newChoices[index] = value;
      setEditChoices(newChoices);
    } else {
      const newChoices = [...choices];
      newChoices[index] = value;
      setChoices(newChoices);
    }
  };

  const handleDeleteQuestion = (id: string) => {
    const updatedQuestions = questions.filter(
      (question) => question.questionId !== id,
    );
    setQuestions(updatedQuestions);
    persistQuestions(updatedQuestions);
  };

  const handleSaveQuestion = () => {
    if (editIndex !== null) {
      const updatedQuestions = questions.map((question) =>
        question.questionId === editIndex
          ? {
            ...question,
            question: editQuestionText,
            questionType: editQuestionType,
            choices:
                editQuestionType === 'multiple-choice'
                  ? editChoices.filter((choice) => choice.trim() !== '')
                  : undefined,
          }
          : question,
      );
      setQuestions(updatedQuestions);
      setEditIndex(null);
      setEditQuestionText('');
      setEditChoices(['']);
      persistQuestions(updatedQuestions);
    }
  };

  const handleSaveQuestions = async () => {
    await persistQuestions(questions);
  };

  const handleEditQuestion = (question: PreScreenQuestion) => {
    setEditIndex(question.questionId);
    setEditQuestionText(question.question);
    setEditQuestionType(question.questionType);
    setEditChoices(question.choices || ['']);
  };

  const handleQuestionChange = (selectedOption: SelectOptions) => {
    const preScreenQuestion = searchQuestions.find(
      (question) => question.id === selectedOption.value,
    );
    if (preScreenQuestion?.id) {
      const duplicateQuestion = questions.find(
        (question) => question.questionId === selectedOption.value,
      );
      if (duplicateQuestion) {
        toast.error('Question already exists in assessment', {
          position: 'bottom-center',
        });
        return;
      }
      let newQuestion: PreScreenQuestion = {
        questionId: preScreenQuestion.id,
        questionType: preScreenQuestion.questionType,
        question: preScreenQuestion.question,
      };
      if (
        preScreenQuestion.questionType === 'multiple-choice' &&
        preScreenQuestion.choices
      ) {
        newQuestion.choices = preScreenQuestion.choices;
      }
      const updatedQuestions = [...questions, newQuestion];
      setQuestions(updatedQuestions);
      persistQuestions(updatedQuestions);
    }
  };

  return {
    assessmentInfo,
    choices,
    createLabel,
    debounce,
    defaultOptions,
    editChoices,
    setNewQuestionType,
    handleSaveQuestions,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleQuestionChange,
    handleChoiceChange,
    handleAddQuestion,
    loadOptionsDebounced,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
  };
};
